import { useState } from 'react';
import {
  FileText, CheckCircle2, Circle, Clock, AlertTriangle, Upload,
  TrendingUp, DollarSign, Users, BarChart3, ChevronRight, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';

// ==================== Helpers ====================

const fmtAUD = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(v);

// ==================== Mock Data ====================

interface SARStep {
  label: string;
  status: 'completed' | 'in-progress' | 'pending';
}

interface FundSAR {
  fundName: string;
  abn: string;
  steps: SARStep[];
}

const MOCK_SAR_FUNDS: FundSAR[] = [
  {
    fundName: 'Smith Family Super Fund',
    abn: '12 345 678 901',
    steps: [
      { label: 'Financial Statements', status: 'completed' },
      { label: 'Member Statements', status: 'completed' },
      { label: 'Tax Calculation', status: 'completed' },
      { label: 'Schedules', status: 'in-progress' },
      { label: 'Lodgement', status: 'pending' },
    ],
  },
  {
    fundName: 'Johnson Retirement Fund',
    abn: '98 765 432 109',
    steps: [
      { label: 'Financial Statements', status: 'completed' },
      { label: 'Member Statements', status: 'in-progress' },
      { label: 'Tax Calculation', status: 'pending' },
      { label: 'Schedules', status: 'pending' },
      { label: 'Lodgement', status: 'pending' },
    ],
  },
];

const FINANCIALS = {
  fundName: 'Smith Family Super Fund',
  operating: {
    income: [
      { label: 'Dividends', amount: 42_500 },
      { label: 'Interest', amount: 18_200 },
      { label: 'Rental Income', amount: 31_200 },
      { label: 'Unrealised Gains', amount: 67_800 },
      { label: 'Realised Gains', amount: 23_400 },
    ],
    expenses: [
      { label: 'Administration Fees', amount: 4_800 },
      { label: 'Audit Fees', amount: 2_500 },
      { label: 'Insurance Premiums', amount: 6_100 },
      { label: 'Investment Management', amount: 8_300 },
      { label: 'ASIC Levy', amount: 259 },
    ],
  },
  position: {
    assets: [
      { label: 'Cash at Bank', amount: 125_000 },
      { label: 'Australian Shares', amount: 520_000 },
      { label: 'International Shares', amount: 180_000 },
      { label: 'Residential Property', amount: 650_000 },
      { label: 'Term Deposits', amount: 200_000 },
      { label: 'Managed Funds', amount: 175_000 },
    ],
    liabilities: [
      { label: 'LRBA Loan - Property', amount: 280_000 },
      { label: 'Provision for Tax', amount: 24_500 },
      { label: 'Accounts Payable', amount: 3_200 },
    ],
  },
};

const MOCK_MEMBERS = [
  {
    name: 'John Smith',
    memberNo: 'M001',
    openingBalance: 820_000,
    contributions: { concessional: 27_500, nonConcessional: 50_000 },
    earnings: 68_200,
    fees: 5_400,
    tax: 12_300,
    closingBalance: 948_000,
    taxComponents: { taxFree: 142_200, taxableTaxed: 780_300, taxableUntaxed: 25_500 },
  },
  {
    name: 'Jane Smith',
    memberNo: 'M002',
    openingBalance: 680_000,
    contributions: { concessional: 27_500, nonConcessional: 0 },
    earnings: 55_100,
    fees: 4_800,
    tax: 9_800,
    closingBalance: 748_000,
    taxComponents: { taxFree: 224_400, taxableTaxed: 510_600, taxableUntaxed: 13_000 },
  },
];

interface AuditItem { label: string; done: boolean }
interface AuditCategory { category: string; items: AuditItem[] }

const AUDIT_CHECKLIST: AuditCategory[] = [
  {
    category: 'Bank & Cash',
    items: [
      { label: 'Bank reconciliations completed', done: true },
      { label: 'Bank statements obtained (all accounts)', done: true },
      { label: 'Petty cash reconciliation', done: false },
    ],
  },
  {
    category: 'Investments',
    items: [
      { label: 'Share holdings confirmed (CHESS/HIN statements)', done: true },
      { label: 'Property valuations obtained (market value)', done: true },
      { label: 'Unlisted asset valuations (s.17A compliant)', done: false },
      { label: 'Managed fund statements', done: true },
    ],
  },
  {
    category: 'Contributions',
    items: [
      { label: 'Member contributions summary', done: true },
      { label: 'Employer contributions (SG statements)', done: true },
      { label: 'Rollover statements', done: false },
    ],
  },
  {
    category: 'Pensions',
    items: [
      { label: 'Pension commencement documentation', done: true },
      { label: 'Payment summaries (PAYG)', done: true },
      { label: 'Minimum pension calculations', done: true },
      { label: 'Commutation authority (if applicable)', done: false },
    ],
  },
  {
    category: 'Compliance',
    items: [
      { label: 'Trust deed (current version)', done: true },
      { label: 'Investment strategy (reviewed & signed)', done: true },
      { label: 'Minutes of meetings', done: true },
      { label: 'Binding death benefit nominations (BDBN)', done: false },
      { label: 'Trustee declarations', done: true },
    ],
  },
];

interface AuditException {
  description: string;
  severity: 'critical' | 'major' | 'minor';
  auditorComment: string;
  status: 'open' | 'rectified' | 'in-progress';
  dueDate: string;
}

const MOCK_EXCEPTIONS: AuditException[] = [
  {
    description: 'Unlisted unit trust valuation not obtained at 30 June',
    severity: 'critical',
    auditorComment: 'Valuation required under s.17A SIS Regulations. Must reflect market value.',
    status: 'open',
    dueDate: '15/01/2027',
  },
  {
    description: 'BDBN for member M002 expired on 14/03/2026',
    severity: 'major',
    auditorComment: 'Non-lapsing BDBN recommended. Current nomination has lapsed.',
    status: 'in-progress',
    dueDate: '28/02/2027',
  },
  {
    description: 'Rollover-in documentation incomplete for FY2026',
    severity: 'minor',
    auditorComment: 'Rollover benefit statement from previous fund not on file.',
    status: 'rectified',
    dueDate: '01/12/2026',
  },
  {
    description: 'Insurance policy cover note not held on record',
    severity: 'minor',
    auditorComment: 'Trustee should retain copy of insurance certificate annually.',
    status: 'open',
    dueDate: '28/02/2027',
  },
];

const BENCHMARK_DATA = [
  { metric: 'Expense Ratio (%)', fund: 0.82, average: 1.15 },
  { metric: 'Return on Assets (%)', fund: 8.4, average: 7.2 },
  { metric: 'Admin Costs ($k)', fund: 4.8, average: 6.3 },
  { metric: 'Diversification Score', fund: 7.5, average: 6.0 },
];

const SEVERITY_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  critical: { label: 'Critical', variant: 'destructive' },
  major: { label: 'Major', variant: 'secondary' },
  minor: { label: 'Minor', variant: 'default' },
};

const STATUS_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  open: { label: 'Open', variant: 'outline' },
  'in-progress': { label: 'In Progress', variant: 'secondary' },
  rectified: { label: 'Rectified', variant: 'default' },
};

// ==================== Component ====================

export default function SMSFReportingPage() {
  const [tab, setTab] = useState('sar');
  const [selectedFundId, setSelectedFundId] = useState<string>('');

  // Fetch funds list
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const fundId = selectedFundId || fundsData?.[0]?.id || '';

  // Fetch audits for selected fund
  const { data: auditsData, isLoading: auditsLoading } = useQuery({
    queryKey: ['smsf-audits', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/audits`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Fetch lodgements for selected fund
  const { data: lodgementsData, isLoading: lodgementsLoading } = useQuery({
    queryKey: ['smsf-lodgements', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/lodgements`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Use API data with mock fallback
  const sarFunds = lodgementsData?.sarFunds || MOCK_SAR_FUNDS;
  const financials = auditsData?.financials || FINANCIALS;
  const mockMembers = auditsData?.members || MOCK_MEMBERS;
  const auditChecklist = auditsData?.checklist || AUDIT_CHECKLIST;
  const auditExceptions = auditsData?.exceptions || MOCK_EXCEPTIONS;
  const benchmarkData = auditsData?.benchmarks || BENCHMARK_DATA;

  const isLoading = fundsLoading || auditsLoading || lodgementsLoading;

  const totalIncome = financials.operating.income.reduce((s: number, i: any) => s + i.amount, 0);
  const totalExpenses = financials.operating.expenses.reduce((s: number, i: any) => s + i.amount, 0);
  const totalAssets = financials.position.assets.reduce((s: number, i: any) => s + i.amount, 0);
  const totalLiabilities = financials.position.liabilities.reduce((s: number, i: any) => s + i.amount, 0);

  const auditTotalItems = auditChecklist.reduce((s: number, c: any) => s + c.items.length, 0);
  const auditDoneItems = auditChecklist.reduce((s: number, c: any) => s + c.items.filter((i: any) => i.done).length, 0);
  const auditOverall = Math.round((auditDoneItems / auditTotalItems) * 100);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileText}
        title="SMSF Reporting & Audit"
        description="Annual return preparation, financial statements, and audit management"
      />

      {/* Fund Selector */}
      {fundsData && fundsData.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-muted-foreground">Fund:</label>
          <Select value={fundId} onValueChange={setSelectedFundId}>
            <SelectTrigger className="w-[320px]"><SelectValue placeholder="Select a fund..." /></SelectTrigger>
            <SelectContent>
              {fundsData.map((f: any) => (
                <SelectItem key={f.id} value={f.id}>{f.fund_name || f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="sar">Annual Return</TabsTrigger>
          <TabsTrigger value="financials">Financial Statements</TabsTrigger>
          <TabsTrigger value="members">Member Statements</TabsTrigger>
          <TabsTrigger value="audit">Audit Pack</TabsTrigger>
          <TabsTrigger value="exceptions">Audit Exceptions</TabsTrigger>
          <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
        </TabsList>

        {/* ===== SAR Tab ===== */}
        <TabsContent value="sar" className="space-y-4">
          {sarFunds.map((fund: any) => {
            const done = fund.steps.filter(s => s.status === 'completed').length;
            const pct = Math.round((done / fund.steps.length) * 100);
            return (
              <Card key={fund.abn}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{fund.fundName}</CardTitle>
                      <CardDescription className="font-mono text-xs">ABN {fund.abn}</CardDescription>
                    </div>
                    <Badge variant={pct === 100 ? 'default' : 'secondary'}>{pct}% Complete</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Progress value={pct} className="h-2 mb-4" />
                  <div className="flex flex-wrap gap-2">
                    {fund.steps.map((step, i) => {
                      const Icon = step.status === 'completed' ? CheckCircle2
                        : step.status === 'in-progress' ? Clock : Circle;
                      const color = step.status === 'completed' ? 'text-green-600'
                        : step.status === 'in-progress' ? 'text-amber-500' : 'text-muted-foreground';
                      return (
                        <div key={i} className="flex items-center gap-1.5 text-sm">
                          <Icon className={cn('h-4 w-4', color)} />
                          <span className={step.status === 'pending' ? 'text-muted-foreground' : ''}>{step.label}</span>
                          {i < fund.steps.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ===== Financial Statements Tab ===== */}
        <TabsContent value="financials" className="space-y-4">
          <p className="text-sm text-muted-foreground">{financials.fundName} — FY2025/26</p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Operating Statement */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" /> Operating Statement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Income</p>
                  {financials.operating.income.map((r: any) => (
                    <div key={r.label} className="flex justify-between text-sm py-0.5">
                      <span>{r.label}</span>
                      <span className="font-mono">{fmtAUD(r.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
                    <span>Total Income</span><span className="font-mono">{fmtAUD(totalIncome)}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Expenses</p>
                  {financials.operating.expenses.map((r: any) => (
                    <div key={r.label} className="flex justify-between text-sm py-0.5">
                      <span>{r.label}</span>
                      <span className="font-mono text-red-600">({fmtAUD(r.amount)})</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
                    <span>Total Expenses</span><span className="font-mono text-red-600">({fmtAUD(totalExpenses)})</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm font-bold border-t-2 pt-2">
                  <span>Net Operating Result</span>
                  <span className="font-mono text-green-700">{fmtAUD(totalIncome - totalExpenses)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Statement of Financial Position */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> Statement of Financial Position
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Assets</p>
                  {financials.position.assets.map((r: any) => (
                    <div key={r.label} className="flex justify-between text-sm py-0.5">
                      <span>{r.label}</span><span className="font-mono">{fmtAUD(r.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
                    <span>Total Assets</span><span className="font-mono">{fmtAUD(totalAssets)}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Liabilities</p>
                  {financials.position.liabilities.map((r: any) => (
                    <div key={r.label} className="flex justify-between text-sm py-0.5">
                      <span>{r.label}</span><span className="font-mono text-red-600">({fmtAUD(r.amount)})</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
                    <span>Total Liabilities</span><span className="font-mono text-red-600">({fmtAUD(totalLiabilities)})</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm font-bold border-t-2 pt-2">
                  <span>Net Assets / Member Benefits</span>
                  <span className="font-mono text-green-700">{fmtAUD(totalAssets - totalLiabilities)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== Member Statements Tab ===== */}
        <TabsContent value="members" className="space-y-4">
          {mockMembers.map((m: any) => (
            <Card key={m.memberNo}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" /> {m.name}
                  </CardTitle>
                  <Badge variant="outline">{m.memberNo}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Movement Summary</p>
                    <div className="flex justify-between text-sm"><span>Opening Balance</span><span className="font-mono">{fmtAUD(m.openingBalance)}</span></div>
                    <div className="flex justify-between text-sm text-green-700"><span>+ Concessional Contributions</span><span className="font-mono">{fmtAUD(m.contributions.concessional)}</span></div>
                    <div className="flex justify-between text-sm text-green-700"><span>+ Non-Concessional Contributions</span><span className="font-mono">{fmtAUD(m.contributions.nonConcessional)}</span></div>
                    <div className="flex justify-between text-sm text-green-700"><span>+ Investment Earnings</span><span className="font-mono">{fmtAUD(m.earnings)}</span></div>
                    <div className="flex justify-between text-sm text-red-600"><span>- Administration Fees</span><span className="font-mono">({fmtAUD(m.fees)})</span></div>
                    <div className="flex justify-between text-sm text-red-600"><span>- Tax</span><span className="font-mono">({fmtAUD(m.tax)})</span></div>
                    <div className="flex justify-between text-sm font-bold border-t pt-1 mt-1">
                      <span>Closing Balance</span><span className="font-mono">{fmtAUD(m.closingBalance)}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Tax Components</p>
                    <div className="space-y-2">
                      {[
                        { label: 'Tax-Free Component', value: m.taxComponents.taxFree, color: 'bg-green-500' },
                        { label: 'Taxable (Taxed)', value: m.taxComponents.taxableTaxed, color: 'bg-blue-500' },
                        { label: 'Taxable (Untaxed)', value: m.taxComponents.taxableUntaxed, color: 'bg-amber-500' },
                      ].map((tc) => (
                        <div key={tc.label}>
                          <div className="flex justify-between text-sm mb-0.5">
                            <span className="flex items-center gap-1.5">
                              <span className={cn('h-2.5 w-2.5 rounded-full', tc.color)} />
                              {tc.label}
                            </span>
                            <span className="font-mono">{fmtAUD(tc.value)}</span>
                          </div>
                          <Progress value={(tc.value / m.closingBalance) * 100} className="h-1.5" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ===== Audit Pack Tab ===== */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Audit Pack Completion</CardTitle>
                <Badge variant={auditOverall === 100 ? 'default' : 'secondary'}>{auditOverall}% Complete</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Progress value={auditOverall} className="h-2.5 mb-6" />

              <div className="space-y-6">
                {auditChecklist.map((cat: any) => {
                  const catDone = cat.items.filter(i => i.done).length;
                  const catPct = Math.round((catDone / cat.items.length) * 100);
                  return (
                    <div key={cat.category}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium">{cat.category}</p>
                        <span className="text-xs text-muted-foreground">{catDone}/{cat.items.length}</span>
                      </div>
                      <Progress value={catPct} className="h-1.5 mb-2" />
                      <div className="space-y-1.5">
                        {cat.items.map((item) => (
                          <div key={item.label} className="flex items-center gap-2 text-sm">
                            {item.done
                              ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                              : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                            <span className={item.done ? '' : 'text-muted-foreground'}>{item.label}</span>
                            {!item.done && (
                              <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs gap-1">
                                <Upload className="h-3 w-3" /> Upload
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Audit Exceptions Tab ===== */}
        <TabsContent value="exceptions" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Audit Exceptions</CardTitle>
              <CardDescription>Issues identified during the audit requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Description</th>
                      <th className="pb-2 pr-4 font-medium">Severity</th>
                      <th className="pb-2 pr-4 font-medium">Auditor Comment</th>
                      <th className="pb-2 pr-4 font-medium">Status</th>
                      <th className="pb-2 font-medium">Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditExceptions.map((ex: any, i: number) => {
                      const sev = SEVERITY_BADGE[ex.severity];
                      const st = STATUS_BADGE[ex.status];
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium max-w-[220px]">{ex.description}</td>
                          <td className="py-3 pr-4"><Badge variant={sev.variant}>{sev.label}</Badge></td>
                          <td className="py-3 pr-4 text-muted-foreground max-w-[280px]">{ex.auditorComment}</td>
                          <td className="py-3 pr-4"><Badge variant={st.variant}>{st.label}</Badge></td>
                          <td className="py-3 text-muted-foreground whitespace-nowrap">{ex.dueDate}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Benchmarks Tab ===== */}
        <TabsContent value="benchmarks" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> Fund vs ATO SMSF Averages
              </CardTitle>
              <CardDescription>Comparison of key fund metrics against the latest ATO SMSF statistical overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={benchmarkData} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="metric" type="category" width={160} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => `${Number(value).toFixed(1)}`}
                      contentStyle={{ borderRadius: 8, fontSize: 13 }} cursor={false}
                    />
                    <Bar dataKey="fund" name="Your Fund" fill="#5048e5" radius={[0, 4, 4, 0]} barSize={16} activeBar={false}/>
                    <Bar dataKey="average" name="ATO Average" fill="#94a3b8" radius={[0, 4, 4, 0]} barSize={16} activeBar={false}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#5048e5]" /> Your Fund</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#94a3b8]" /> ATO Average</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
